class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.addEventListener('keydown', this.keepFocusInDrawer.bind(this), true);
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.updateConsentSpace = this.updateConsentSpace.bind(this);
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    setTimeout(() => {
      this.classList.add('animate', 'active');
      removeTrapFocus();
      requestAnimationFrame(() => requestAnimationFrame(() => {
        const dialog = this.querySelector('.drawer__inner');
        if (this.classList.contains('active') && !dialog?.contains(document.activeElement)) {
          dialog?.focus({ preventScroll: true });
        }
      }));
    });

    document.body.classList.add('overflow-hidden');
    this.watchConsentSpace();
  }

  keepFocusInDrawer(event) {
    if (event.key !== 'Tab' || !this.classList.contains('active')) return;
    const dialog = this.querySelector('.drawer__inner');
    if (!dialog?.contains(event.target)) return;

    // Reviews and cart updates add controls after opening; use the current visible set.
    const controls = getFocusableElements(dialog).filter((element) =>
      element.tabIndex >= 0 && !element.closest('[inert]') &&
      element.getClientRects().length && getComputedStyle(element).visibility === 'visible'
    );
    const first = controls[0];
    const last = controls[controls.length - 1];
    event.stopPropagation();
    if (!first) {
      event.preventDefault();
      dialog.focus();
    } else if (event.shiftKey && (event.target === first || event.target === dialog)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && event.target === last) {
      event.preventDefault();
      first.focus();
    }
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    this.stopWatchingConsentSpace();
  }

  watchConsentSpace() {
    this.stopWatchingConsentSpace();
    this.consentResizeObserver = new ResizeObserver(this.updateConsentSpace);
    this.consentMutationObserver = new MutationObserver(this.updateConsentSpace);
    this.consentMutationObserver.observe(document.body, { childList: true, subtree: true });
    window.addEventListener('resize', this.updateConsentSpace);
    window.visualViewport?.addEventListener('resize', this.updateConsentSpace);
    this.updateConsentSpace();
  }

  updateConsentSpace() {
    const banners = document.querySelectorAll('#kc-consent-banner, #shopify-pc__banner, .shopify-pc__banner');
    const viewportHeight = window.visualViewport?.height || window.innerHeight;
    let space = 0;
    banners.forEach((banner) => {
      this.consentResizeObserver?.observe(banner);
      const rect = banner.getBoundingClientRect();
      // Reserve only the part of a visible bottom banner that overlaps the viewport.
      if (rect.height && rect.bottom >= viewportHeight - 2 && rect.top < viewportHeight &&
          getComputedStyle(banner).visibility !== 'hidden') {
        space = Math.max(space, viewportHeight - Math.max(0, rect.top));
      }
    });
    const value = Math.ceil(space) + 'px';
    if (this.style.getPropertyValue('--kanen-cart-consent-height') !== value) {
      this.style.setProperty('--kanen-cart-consent-height', value);
    }
  }

  stopWatchingConsentSpace() {
    this.consentResizeObserver?.disconnect();
    this.consentMutationObserver?.disconnect();
    window.removeEventListener('resize', this.updateConsentSpace);
    window.visualViewport?.removeEventListener('resize', this.updateConsentSpace);
  }

  disconnectedCallback() {
    this.stopWatchingConsentSpace();
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');

    if (parsedState && parsedState.id) {
      this.productId = parsedState.id;
    }

    this.getSectionsToRender()?.forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;

      if (parsedState && parsedState?.sections) {
        sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      }
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
